import { ElementRef, Injectable } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subject } from 'rxjs';
import { WIDGET_CONSTANTS } from '../constants/widget-constants';
import { PartnerDao } from '../db-models/widget-conf-dao';
import { CustomerProfile, GlobalObjects, Partner } from '../models/global';
import { WidgetColorConf } from '../models/widget-color.model';
import { WidgetConf } from '../models/widget-conf';
import { LoggerService } from './logger.service';
import { PartnerService } from './partner.service';

@Injectable({
  providedIn: 'root'
})
export class WidgetUtilService {

  widgetConf: WidgetConf;
  zipCode: string;
  // The normalised store-search postcode the personal form last wrote into the customer zip.
  zipFilledFromStorePostcode: string | null = null;
  customerProfile: CustomerProfile;

  // Property to store parent URL received via postMessage
  parentUrl: string = '';

  // Property to store Stripe redirect parameters received from parent page
  stripeRedirectParams: {
    payment_intent?: string;
    payment_intent_client_secret?: string;
    redirect_status?: string;
  } = {};

  // Subject to notify when Stripe params are received from parent
  stripeRedirectParamsReceived$ = new Subject<{
    payment_intent?: string;
    payment_intent_client_secret?: string;
    redirect_status?: string;
  }>();

  /** Records the postcode the customer entered in the store search. */
  setStoreSearchZipCode(next: string): void {
    this.zipCode = next;
  }

  /** Clears the store-search postcode and the remembered fill, e.g. for the next booking. */
  resetStoreSearchZipCode(): void {
    this.zipCode = '';
    this.zipFilledFromStorePostcode = null;
  }

  /**
   * Initialize parent URL detection on app startup.
   * Call this once in main component's ngOnInit.
   */
  initParentUrl(): void {
    // Check if we're in an iframe
    const isInIframe = window !== window.parent;

    if (isInIframe) {
      // Listen for parent URL response
      window.addEventListener('message', (event) => {
        // Handle parent URL response
        if (event?.data?.type === 'PARENT_URL_RESPONSE' && event?.data?.parentUrl) {
          this.parentUrl = event.data.parentUrl;
          LoggerService.log('[Parent URL] Received via postMessage:', this.parentUrl);

          // Parse the parent URL to check for Stripe redirect parameters
          this.extractStripeParamsFromUrl(this.parentUrl);
        }
      });

      // Request parent URL via postMessage
      try {
        window.parent.postMessage({ eventName: 'REQUEST_PARENT_URL' }, '*');
        LoggerService.log('[Parent URL] Requested from parent via postMessage');
      } catch (error) {
        LoggerService.warn('[Parent URL] Failed to request from parent:', error);
      }
    }
  }

  /**
   * Extract Stripe redirect parameters from a URL string.
   * Called when parent URL is received to check if it's a Stripe redirect.
   */
  private extractStripeParamsFromUrl(url: string): void {
    try {
      const urlObj = new URL(url);
      const payment_intent = urlObj.searchParams.get('payment_intent');
      const payment_intent_client_secret = urlObj.searchParams.get('payment_intent_client_secret');
      const redirect_status = urlObj.searchParams.get('redirect_status');

      if (payment_intent && payment_intent_client_secret) {
        this.stripeRedirectParams = {
          payment_intent,
          payment_intent_client_secret,
          redirect_status
        };
        LoggerService.log('[Stripe Redirect] Extracted params from parent URL:', this.stripeRedirectParams);

        // Emit so subscribers can react to the params
        this.stripeRedirectParamsReceived$.next(this.stripeRedirectParams);
      }
    } catch (error) {
      LoggerService.warn('[Stripe Redirect] Failed to parse parent URL:', error);
    }
  }

  /**
   * Get Stripe redirect parameters received from parent page.
   */
  getStripeRedirectParams(): { payment_intent?: string; payment_intent_client_secret?: string; redirect_status?: string } {
    return this.stripeRedirectParams;
  }

  /**
   * Check if we have Stripe redirect parameters from parent.
   */
  hasStripeRedirectParams(): boolean {
    return !!this.stripeRedirectParams.payment_intent;
  }

  /**
   * Get the return URL for Stripe payments with fallback chain:
   * 1. parentUrl from postMessage (set by parent page)
   * 2. window.location.href (final fallback)
   *
   * IMPORTANT: Cleans Stripe redirect params to prevent duplicate params
   * when Stripe appends its own params on redirect.
   */
  getStripeReturnUrl(): string {
    let url: string;

    // Priority 1: Parent URL from postMessage
    if (this.parentUrl) {
      url = this.parentUrl;
    } else {
      // Priority 2: Current window location (final fallback)
      url = window.location.href;
    }

    // Clean Stripe redirect params to prevent duplicates
    // When Stripe redirects back, it appends these params to the return_url
    // If the URL already contains these params, we get duplicates
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.delete('payment_intent');
      urlObj.searchParams.delete('payment_intent_client_secret');
      urlObj.searchParams.delete('redirect_status');
      url = urlObj.toString();
    } catch (e) {
      LoggerService.warn('[Stripe Return URL] Failed to clean URL:', e);
    }

    LoggerService.log('[Stripe Return URL] Returning clean URL:', url);
    return url;
  }

  constructor(
    private partnerService: PartnerService
  ) {
  }

  createPartnerGlobalData(widgetConf: WidgetConf, language: string, partnerDao: PartnerDao): Partner {
    const partner = new Partner();
    partner.id = widgetConf.id;
    partner.uuid = widgetConf.uuid;
    partner.name = widgetConf.name;
    partner.bookingName = widgetConf.bookingName;
    partner.logo = widgetConf.logo;
    partner.currency = widgetConf.currency;
    partner.url = widgetConf.url;
    partner.languageCode = language;
    partner.showEgalWer = widgetConf.showEgalWer;
    partner.needCustomerAddress = widgetConf.needCustomerAddress;
    partner.show_message_on_widget = widgetConf.show_message_on_widget;
    partner.needPhoneNumber = widgetConf.needPhoneNumber;
    partner.needMobileNumber = widgetConf.needMobileNumber;
    partner.countryCode = widgetConf.countryCode;
    partner.courtesyForm = widgetConf.courtesyForm;
    partner.appointmentLeadTime = widgetConf.appointmentLeadTime;
    partner.eventLeadTime = widgetConf.eventLeadTime;
    partner.appointmentBookingInfo = widgetConf.appointmentBookingInfo;
    partner.eventSlotBookingInfo = widgetConf.eventSlotBookingInfo;
    partner.hide_login_on_bookingsite = widgetConf.hide_login_on_bookingsite;
    partner.hide_back_on_bookingsite = widgetConf.hide_back_on_bookingsite;
    partner.widget_buttons_always_enabled = widgetConf.widget_buttons_always_enabled;
    partner.hide_book_additional_appointment_button = widgetConf.hide_book_additional_appointment_button;
    partner.subscription_type_id = widgetConf.subscriptionTypeId;
    partner.allow_multiple_services_bookings = widgetConf.allow_multiple_services_bookings;
    partner.hide_resource_step = Number(partnerDao.hide_resource_step);
    partner.hide_mail_resource = Number(partnerDao.hide_mail_resource);
    partner.future_booking_threshold_appointments = partnerDao.future_booking_threshold_appointments;
    partner.future_booking_threshold_events = partnerDao.future_booking_threshold_events;
    partner.widget_appointment_service_category_status = partnerDao.widget_appointment_service_category_status;
    partner.supported_widget_languages = partnerDao.supported_widget_languages;
    partner.language_identifier = partnerDao.language_identifier;
    // partner.templates = partnerDao.templates;
    partner.enable_store_postcode_feature = Number(partnerDao.enable_store_postcode_feature);
    // partner.enable_store_postcode_feature = 1;
    partner.ask_for_customer_notification_preference = partnerDao.ask_for_customer_notification_preference;
    partner.successful_booking_redirect_url = partnerDao.successful_booking_redirect_url;
    partner.redirect_after_booking_action = partnerDao.redirect_after_booking_action;
    // partner.ask_for_customer_notification_preference = 1;
    partner.disable_automatic_search = Number(partnerDao.disable_automatic_search);
    partner.enable_services_search = Number(partnerDao.enable_services_search);
    partner.widget_hide_resource_step_for_default_workers = Number(partnerDao.widget_hide_resource_step_for_default_workers);
    partner.widget_disable_search_all_resource_button = Number(partnerDao.widget_disable_search_all_resource_button);
    partner.activate_phone_number_verification_feature = Number(partnerDao.activate_phone_number_verification_feature);
    partner.activate_mobile_number_verification_feature = Number(partnerDao.activate_mobile_number_verification_feature);
    partner.widget_email_shown = Number(partnerDao.widget_email_shown);
    partner.widget_email_required = Number(partnerDao.widget_email_required);
    partner.widget_show_lead_creation_button = Number(partnerDao.widget_show_lead_creation_button);
    partner.show_distance_in_stores = Number(partnerDao.show_distance_in_stores);
    partner.automatic_store_zip_search = Number(partnerDao.automatic_store_zip_search);
    partner.show_salutation_in_widget = Number(partnerDao.show_salutation_in_widget);
    partner.internal_widget_email_shown = Number(partnerDao.internal_widget_email_shown);
    partner.internal_widget_email_required = Number(partnerDao.internal_widget_email_required);
    partner.show_inline_description = Number(partnerDao.show_inline_description);
    partner.is_maintenance_mode_enabled = Number(partnerDao.is_maintenance_mode_enabled);
    partner.show_privacy_message_checkbox = Number(partnerDao.show_privacy_message_checkbox);
    partner.is_round_robin_feature_enabled = Number(partnerDao.is_round_robin_feature_enabled);
    partner.is_widget_anyone_exception_enabled = Number(partnerDao.is_widget_anyone_exception_enabled)
    partner.is_widget_multiple_workers_enabled = Number(partnerDao.is_widget_multiple_workers_enabled);
    partner.widget_address_required = Number(partnerDao.widget_address_required);
    partner.is_customer_profiles_feature_enabled = Number(partnerDao.is_customer_profiles_feature_enabled);
    partner.customer_profiles = partnerDao.customer_profiles;
    partner.widget_salutation_required = Number(partnerDao.widget_salutation_required);
    partner.widget_phone_required = Number(partnerDao.widget_phone_required);
    partner.widget_message_required = Number(partnerDao.widget_message_required);
    partner.internal_widget_store_search_list = Number(partnerDao.internal_widget_store_search_list);
    partner.hide_event_capacity = Number(partnerDao.hide_event_capacity);
    partner.widget_company_name_required = Number(partnerDao.widget_company_name_required);
    partner.widget_company_name_shown = Number(partnerDao.widget_company_name_shown);
    partner.widget_internal_comment_required = Number(partnerDao.widget_internal_comment_required);
    partner.widget_internal_comment_shown = Number(partnerDao.widget_internal_comment_shown);
    partner.is_redirect_after_booking_enabled = Number(partnerDao.is_redirect_after_booking_enabled);
    partner.is_widget_banner_hint_enabled = Number(partnerDao.is_widget_banner_hint_enabled);
    partner.widget_banner_hint_color = partnerDao.widget_banner_hint_color;
    partner.widget_banner_hint_text = partnerDao.widget_banner_hint_text;
    partner.anyone_feature_hide_ressource = partnerDao.anyone_feature_hide_ressource;
    partner.widget_title_required = Number(partnerDao.widget_title_required);
    partner.widget_title_shown = Number(partnerDao.widget_title_shown);
    partner.widget_country_required = Number(partnerDao.widget_country_required);
    partner.widget_country_shown = Number(partnerDao.widget_country_shown);
    partner.widget_show_resource_store_name = Number(partnerDao.widget_show_resource_store_name);
    partner.is_group_appointment_store_feature_enabled = Number(partnerDao.is_group_appointment_store_feature_enabled);
    partner.is_group_appointment_category_feature_enabled = Number(partnerDao.is_group_appointment_category_feature_enabled);
    partner.is_widget_event_single_slot_preselect_enabled = Number(partnerDao.is_widget_event_single_slot_preselect_enabled);
    partner.widget_mobile_required = Number(partnerDao.widget_mobile_required);
    partner.widget_show_location_on_appointment_proposal = Number(partnerDao.widget_show_location_on_appointment_proposal);
    partner.is_widget_service_shortcut_button_enabled = Number(partnerDao.is_widget_service_shortcut_button_enabled);
    partner.include_city_name_in_postcode_results = Number(partnerDao.include_city_name_in_postcode_results);
    partner.widget_show_free_slots_in_overview = Number(partnerDao.widget_show_free_slots_in_overview);
    partner.show_worker_selection_on_date_step = Number(partnerDao.show_worker_selection_on_date_step);
    partner.is_widget_postcode_worker_random_selection_enabled = Number(partnerDao.is_widget_postcode_worker_random_selection_enabled);
    partner.show_widget_slot_location = Number(partnerDao.show_widget_slot_location);
    partner.widget_jumps_to_resources_step_when_single_service = Number(partnerDao.widget_jumps_to_resources_step_when_single_service);
    partner.group_appointments_filter_behaviour = partnerDao.group_appointments_filter_behaviour;
    partner.meeting_type_selection_ui_mode = partnerDao.meeting_type_selection_ui_mode;
    partner.is_appointment_reservation_enabled = partnerDao.is_appointment_reservation_enabled;
    partner.x_minutes_appointment_reservation = partnerDao.x_minutes_appointment_reservation;
    partner.split_personal_form_and_summary_in_widget = partnerDao.split_personal_form_and_summary_in_widget;
    partner.show_cross_selling_services_on_summary_step = partnerDao.show_cross_selling_services_on_summary_step;
    partner.cross_selling_services_category_filter = Number(partnerDao.cross_selling_services_category_filter ?? 0);
    partner.show_widget_calendar_notice = partnerDao.show_widget_calendar_notice;
    partner.widget_birthday_shown = partnerDao.widget_birthday_shown;
    partner.widget_birthday_required = partnerDao.widget_birthday_required;
    partner.widget_birthday_minimum_age_18 = partnerDao.widget_birthday_minimum_age_18;
    partner.widget_vat_type = partnerDao.widget_vat_type;
    partner.enable_phishing_protection = partnerDao.enable_phishing_protection;
    partner.phishing_protection_regex = partnerDao.phishing_protection_regex;
    partner.is_captcha_feature_enabled = Number(partnerDao.is_captcha_feature_enabled);
    partner.captcha_type = partnerDao.captcha_type;
    partner.friendly_captcha_sitekey = partnerDao.friendly_captcha_sitekey;
    partner.enable_workers_randomize_with_same_time_slots = Number(partnerDao.enable_workers_randomize_with_same_time_slots);
    partner.widget_remove_duplicate_minutes_slots = Number(partnerDao.widget_remove_duplicate_minutes_slots);
    partner.is_zip_codes_search_enabled = Number(partnerDao.is_zip_codes_search_enabled);
    partner.phone = partnerDao.phone;
    partner.timezone = partnerDao.timezone;
    partner.widget_display_timezone_source = partnerDao.widget_display_timezone_source;

    return this.validatePartnerFeatures(partner);
  }

  validatePartnerFeatures(partner: Partner): Partner {
    partner.is_round_robin_feature_enabled = Number(this.partnerService.hasFeature('is_round_robin_enabled') && partner.is_round_robin_feature_enabled);
    LoggerService.warn('[feature] Final value of feature is_round_robin_feature_enabled', partner.is_round_robin_feature_enabled);

    partner.is_redirect_after_booking_enabled = Number(
      this.partnerService.hasFeature('is_external_success_page_forwarding_enabled') && partner.is_redirect_after_booking_enabled
    );
    LoggerService.warn(
      '[feature] Final value of feature is_external_success_page_forwarding_enabled',
      partner.is_redirect_after_booking_enabled
    );

    partner.is_widget_banner_hint_enabled = Number(
      this.partnerService.hasFeature('is_widget_hint_enabled') && partner.is_widget_banner_hint_enabled
    );
    LoggerService.warn('[feature] Final value of feature is_widget_hint_enabled', partner.is_widget_banner_hint_enabled);

    partner.is_timezone_enabled = Number(this.partnerService.hasFeature('is_timezone_enabled'));
    LoggerService.warn('[feature] Final value of feature is_timezone_enabled', partner.is_timezone_enabled);

    return partner;
  }

  createWidgetColorConf(partner: PartnerDao): WidgetColorConf {
    const widgetColorConf: WidgetColorConf = new WidgetColorConf();
    widgetColorConf.widget_header_background_color = partner.widget_header_background_color;
    widgetColorConf.widget_footer_background_color = partner.widget_footer_background_color;
    widgetColorConf.widget_text_color = partner.widget_text_color;
    widgetColorConf.widget_footer_button_color = partner.widget_footer_button_color;
    widgetColorConf.widget_header_active_color = partner.widget_header_active_color;
    widgetColorConf.widget_background_color = partner.widget_background_color;
    return widgetColorConf;
  }

  // TODO(Pratik): We can remove widgetConf from this method
  createGlobalObjectData(
    widgetConf: WidgetConf,
    eventId: string,
    isInternal: boolean,
    isWhitelabeled: boolean,
    user_language: string,
    debugMode = false,
    internalToken = ''
  ): GlobalObjects {
    const globals = new GlobalObjects();

    globals.isInternal = isInternal;
    globals.debugMode = debugMode;
    globals.internalToken = internalToken;
    globals.isWhitelabeled = isWhitelabeled;
    globals.customers = null;
    globals.eventId = eventId || null;
    globals.user_browser_language = user_language;
    globals.user_selected_language = user_language;

    return globals;
  }

  setWidgetConf(widgetConf: WidgetConf): void {
    this.widgetConf = widgetConf;
  }

  getWidgetConf(): WidgetConf {
    return this.widgetConf;
  }

  getBookingTypes(bookingType: string, widgetConf: WidgetConf): string[] {
    const bookTypesEnabled = [];
    switch (bookingType?.toLowerCase()) {
      case WIDGET_CONSTANTS.APPOINTMENT:
      case WIDGET_CONSTANTS.APPOINTMENTS:
        bookTypesEnabled.push(WIDGET_CONSTANTS.APPOINTMENT);
        break;

      case WIDGET_CONSTANTS.EVENT:
      case WIDGET_CONSTANTS.EVENTS:
        bookTypesEnabled.push(WIDGET_CONSTANTS.EVENT);
        break;

      case WIDGET_CONSTANTS.RESOURCE:
      case WIDGET_CONSTANTS.RESOURCES:
        bookTypesEnabled.push(WIDGET_CONSTANTS.RESOURCE);
        break;

      case WIDGET_CONSTANTS.PRE_DEFINED_SLOT:
      case WIDGET_CONSTANTS.PRE_DEFINED_SLOTS:
        bookTypesEnabled.push(WIDGET_CONSTANTS.PRE_DEFINED_SLOT);
        break;

      case WIDGET_CONSTANTS.TOUR:
        bookTypesEnabled.push(WIDGET_CONSTANTS.TOUR);
        break;

      case WIDGET_CONSTANTS.ANY:
        // Create available bookings array
        if (widgetConf.appointmentBookingEnabled) {
          bookTypesEnabled.push(WIDGET_CONSTANTS.APPOINTMENT);
        }
        if (widgetConf.eventBookingEnabled) {
          bookTypesEnabled.push(WIDGET_CONSTANTS.EVENT);
        }
        if (widgetConf.resourceBookingEnabled) {
          bookTypesEnabled.push(WIDGET_CONSTANTS.RESOURCE);
        }
        break;
      default:
        bookTypesEnabled.push(WIDGET_CONSTANTS.APPOINTMENT);
    }
    return bookTypesEnabled;
  }

  sendMessageToParent(messageObject: { [key: string]: any }): void {
    if (window !== window.parent) {
      window.parent.postMessage(messageObject, '*');
    }
  }

  /**
   * Mark all controls in a form as touched to trigger validation UI.
   */
  markFormGroupTouched(form: NgForm): void {
    Object.values(form.controls).forEach((control) => {
      control.markAsTouched();
    });
  }

  /**
   * Common handler for invalid form state: log errors, mark all controls
   * as touched, and scroll/focus the first invalid element.
   * Callers should run their own payment validation after this.
   */
  handleFormInvalidState(form: NgForm, elementRef: ElementRef<any>): void {
    LoggerService.warn('[Validation] Form Validation failed');

    const errorSummary: { [key: string]: any } = {};
    Object.keys(form.controls)
      .filter(controlKey => form.controls[controlKey].invalid)
      .forEach(controlKey => errorSummary[controlKey] = form.controls[controlKey].errors);
    LoggerService.warn(errorSummary);

    this.markFormGroupTouched(form);
    this.focusInvalidElements(elementRef);
  }

  focusInvalidElements(elementRef: ElementRef<any>): void {
    // Assuming you have a reference to the element
    const focusableElements: NodeListOf<HTMLElement> = elementRef.nativeElement.querySelectorAll('input, textarea, ng-select, app-cw-intl-tel-input');

    // Find the first element with the 'ng-invalid' class
    let invalidElement: HTMLElement | undefined = Array.from(focusableElements).find(element =>
      element.classList.contains('ng-invalid')
    );

    // if the element is hidden, scroll to the nearest parent with the 'hidden-field' class
    if (invalidElement.hidden && invalidElement.parentElement?.classList.contains('hidden-field')) {
      invalidElement = invalidElement.parentElement;
    }

    invalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      invalidElement.focus();
    }, 500);
  }
}
